import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, message } from "antd";
import NotificationForm from "../Form";
import { useTranslation } from "react-i18next";
import {
  NotificationCreateParams,
  NotificationStatus,
  useNotificationCreate,
  useNotificationUpdate,
  Notification,
} from "../../../../../hooks/api/notifications";
import { useMessageError } from "../../../../../hooks/common";
import { AnyObject } from "yup";

interface NotificationModalProps {
  open: boolean;
  mode: "create" | "edit";
  onClose: () => void;
  setTableKey?: () => void;
  initialState?: Notification;
}

export default function NotificationModal({
  open,
  mode,
  onClose,
  setTableKey,
  initialState,
}: NotificationModalProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const notificationCreate = useNotificationCreate();
  const notificationUpdate = useNotificationUpdate();
  const [submitType, setSubmitType] = useState<NotificationStatus>(
    NotificationStatus.sent,
  );
  const initialValuesRef = useRef<AnyObject>(null);

  const handleSubmit = async (values: NotificationCreateParams) => {
    const payload: NotificationCreateParams = {
      ...values,
      status: submitType,
    };

    try {
      const res =
        mode === "edit" && initialState?.id
          ? await notificationUpdate.fetch(payload, initialState.id)
          : await notificationCreate.fetch(payload);

      if (res) {
        message.success(
          submitType === NotificationStatus.draft
            ? t("pages.notificationCenter.savedAsDraft")
            : t("pages.notificationCenter.sentSuccessfully"),
        );

        onClose();
        setTableKey?.();
      }
    } catch (error: any) {
      message.error(error?.message);
    }
  };

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && initialState) {
      form.setFieldsValue(initialState);
      initialValuesRef.current = initialState;
      setSubmitType(initialState.status);
    }

    if (mode === "create") {
      initialValuesRef.current = null;
    }
  }, [open, mode, initialState]);

  useMessageError([notificationCreate]);

  const footer = [
    <Button key="cancel" onClick={onClose}>
      {t("common.cancel")}
    </Button>,

    <Button
      key="send"
      type="primary"
      onClick={() => {
        setSubmitType(NotificationStatus.sent);
        form.submit();
      }}
    >
      {t("common.send")}
    </Button>,
  ];

  return (
    <Modal
      title={t("pages.notificationCenter.sendNotification")}
      open={open}
      onCancel={() => {
        // form.resetFields();
        onClose();
      }}
      destroyOnHidden
      width={520}
      footer={footer}
    >
      <Form
        form={form}
        layout="vertical"
        name="notificationForm"
        onFinish={handleSubmit}
      >
        <NotificationForm />
      </Form>
    </Modal>
  );
}
